/**
 * B709 — the `/memberships` controller: whose page (`?tenant=` or a site
 * key), the guest door (`session.guest` mints the CUSTOMER token and hands
 * back the venue's name and logo), then `membership.plan.list` and the
 * tenant's switches (`membership.settings.get`, B709 (i)) under that token.
 * Leg 1 compared the plans; (ii) sells them (636:2): CHOOSE opens WHO'S
 * COVERED, CONTINUE TO PAYMENT asks `membership.buy` — its first answer is
 * the payment provider's card page, so the draft rides this tab's session
 * storage across the redirect — and the provider sends the buyer back to
 * `?buy=<id>`, where the same door charges the saved card and answers the
 * membership (DONE). A refused card brings the buyer back to the form with
 * the provider's answer in words and the card page one press away.
 *
 * B824 — `?presentation=dialog` is the embed's booking dialog (a host button
 * carrying `data-transact-memberships`): the page speaks the bridge, wears
 * the sheet head (‹ back from WHO'S COVERED, ✕ posts `close` to the pinned
 * host) instead of the venue bar, and keeps the buyer in the frame — no
 * Tickets doors, the portal in a tab of its own, and the card's way back
 * returns to the dialog presentation.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { SheetHead } from "@transact/ui";
import { connectBridge, hostErrors, type Bridge, type BridgeWindowLike } from "../lib/bridge.js";
import { ApiError, createApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import { meVenueFacts, type MeVenueFacts } from "../lib/me.js";
import { paintRememberedTheme, paintVenueTheme } from "../lib/paintVenueTheme.js";
import { membershipsOffer, offerPlans, venueBrand, type MembershipsRoute, type OfferPlan, type OfferTier, type Term } from "../lib/membershipsPage.js";
import {
  NO_SWITCHES,
  buyAnswer,
  buyBody,
  buyReturnUrl,
  buyerSwitches,
  checkoutIssues,
  checkoutQuote,
  cardStepNote,
  draftTier,
  freshDraft,
  newBuyId,
  readStash,
  renewalConsent,
  stashKey,
  tierPick,
  type BuyAnswer,
  type BuyerSwitches,
  type CheckoutDraft,
} from "../lib/membershipCheckout.js";
import { MembershipCardStep, MembershipCheckout, MembershipDone, MembershipWorking } from "./membershipCheckout.js";
import { createSetupGateway, loadStripeFactory, type StripeFactory } from "../lib/gateway.js";
import { documentPaymentAppearance } from "../lib/paymentAppearance.js";
import { formatMoney } from "../lib/money.js";
import { MembershipsPage, MembershipsShell, type MembershipsState } from "./membershipsPage.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

const LOAD_REFUSED = "The memberships could not be loaded just now. Try again in a moment.";
const NOT_CONFIRMED = "We couldn't confirm your membership just now. Press Continue to payment again — you won't be charged twice.";
const CARD_NOT_SAVED = "Your card wasn't added, so nothing was charged. Continue to payment to add it.";
const LOST = "This device no longer has the details of that membership, so it wasn't finished — nothing was charged. Choose your plan again to start over.";

type View =
  | { readonly kind: "offer"; readonly lost?: boolean }
  | { readonly kind: "checkout"; readonly buyId: string; readonly draft: CheckoutDraft; readonly pressed: boolean; readonly busy: boolean; readonly banner?: { readonly tone: "danger" | "info"; readonly text: string } }
  | { readonly kind: "paying" }
  /** B816 — the card step ON the page: the Payment Element against the buy's setup secret. */
  | { readonly kind: "card"; readonly buyId: string; readonly draft: CheckoutDraft; readonly clientSecret: string; readonly tierName: string; readonly busy: boolean; readonly error?: string | undefined }
  | { readonly kind: "finishing"; readonly buyId: string }
  | { readonly kind: "done"; readonly answer: Extract<BuyAnswer, { kind: "bought" }>; readonly tierName?: string; readonly email: string };

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function MembershipsApp(props: {
  readonly route: MembershipsRoute;
  readonly apiBaseUrl: string;
  /** A test seam: the API client for a tenant, with or without the guest token. */
  readonly client?: (tenantId: string, token?: string) => Caller;
  /** Test seams: the clock, the redirect to the card page, and this page's own address. */
  readonly now?: () => number;
  readonly go?: (url: string) => void;
  readonly here?: { readonly origin: string; readonly pathname: string };
  /** B816 test seams: force the in-page ask, and the Stripe factory the card step mounts with. */
  readonly inPageCard?: boolean;
  readonly stripeFactory?: () => Promise<StripeFactory | undefined>;
  /** B824 test seam: the frame's window (its parent is the host page). */
  readonly frame?: BridgeWindowLike;
}): ReactElement {
  const client = useCallback(
    (tenantId: string, token?: string): Caller =>
      props.client?.(tenantId, token) ??
      (createApiClient({ baseUrl: props.apiBaseUrl, tenantId, ...(token !== undefined ? { token: () => token } : {}) }) as unknown as Caller),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam is fixed for the page's life
    [props.apiBaseUrl],
  );
  const now = props.now ?? Date.now;
  /* B824 — inside the embed's dialog: the bridge to the host page (the ready ping, the ✕'s close). */
  const dialog = props.route.presentation === "dialog";
  const bridge = useRef<Bridge | undefined>(undefined);
  useEffect(() => {
    if (!dialog) return;
    const b = connectBridge(props.frame ?? (window as unknown as BridgeWindowLike), () => undefined, hostErrors);
    bridge.current = b;
    return () => b.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the frame is fixed for the page's life
  }, [dialog]);
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown",
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);

  const [venue, setVenue] = useState<MeVenueFacts>({});
  const [stripeFacts, setStripeFacts] = useState<{ readonly publishableKey?: string; readonly stripeAccount?: string }>({});
  const [token, setToken] = useState<string | undefined>(undefined);
  const [plans, setPlans] = useState<readonly OfferPlan[] | undefined>(undefined);
  const [switches, setSwitches] = useState<BuyerSwitches>(NO_SWITCHES);
  const [failed, setFailed] = useState(false);
  const [term, setTerm] = useState<Term>("ANNUAL");
  const [picked, setPicked] = useState<string | undefined>(undefined);
  /* B709 (a3) — 632:2's "Give as a gift": CHOOSE opens the checkout with the gift on */
  const [giving, setGiving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<View>(() => (props.route.buyId !== undefined ? { kind: "finishing", buyId: props.route.buyId } : { kind: "offer" }));

  useEffect(() => {
    if (typeof document !== "undefined") paintRememberedTheme(); // B819
  }, []);
  useEffect(() => {
    if (typeof scope !== "object") return;
    let gone = false;
    setFailed(false);
    void (async () => {
      try {
        const guest = (await client(scope.tenantId).call("session.guest", { channelId: scope.channelId })) as { token?: unknown };
        if (gone) return;
        const facts = meVenueFacts(guest);
        setVenue(facts);
        paintVenueTheme(facts.theme); // B819 — the venue's colors, as the hosted page wears them
        /* B816 — the channel's Stripe facts: with a publishable key the card is collected on this page. */
        const g = guest as { publishableKey?: unknown; stripeAccount?: unknown };
        setStripeFacts({
          ...(typeof g.publishableKey === "string" && g.publishableKey !== "" ? { publishableKey: g.publishableKey } : {}),
          ...(typeof g.stripeAccount === "string" && g.stripeAccount !== "" ? { stripeAccount: g.stripeAccount } : {}),
        });
        const t = typeof guest.token === "string" ? guest.token : undefined;
        setToken(t);
        const [read, sw] = await Promise.all([
          client(scope.tenantId, t).call("membership.plan.list", {}),
          // The switches only shape sentences: a refused read keeps them all off rather than failing the page.
          client(scope.tenantId, t)
            .call("membership.settings.get", {})
            .catch(() => undefined),
        ]);
        if (gone) return;
        setSwitches(buyerSwitches(sw));
        setPlans(offerPlans(read));
      } catch {
        if (!gone) setFailed(true);
      }
    })();
    return () => {
      gone = true;
    };
  }, [scope, client, attempt]);

  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;
  /* B709 (a3) — a business with no name brands the bar with its own id, never "Memberships" twice */
  const brand = venueBrand(venue, tenantId);
  const here = props.here ?? (typeof window !== "undefined" ? { origin: window.location.origin, pathname: window.location.pathname } : { origin: "", pathname: "/memberships" });
  /** The card page's way back — into the dialog again when the page lives in the embed's frame (B824). */
  const returnTo = (id: string, buyId: string): string => `${buyReturnUrl(here.origin, here.pathname, id, buyId)}${dialog ? "&presentation=dialog" : ""}`; // vocab-ok: a URL's query key, never rendered

  /** B816/B828 — the channel takes the card on this page: what the buy asks for, and what WHO'S COVERED promises. */
  const cardOnPage = props.inPageCard ?? stripeFacts.publishableKey !== undefined;

  /** One call to the buyer's door with a draft; the answer moves the view. */
  const buy = useCallback(
    async (buyId: string, draft: CheckoutDraft, from: "press" | "return"): Promise<void> => {
      if (tenantId === undefined || plans === undefined) return;
      const pick = draftTier(plans, draft);
      if (pick === undefined) {
        setView({ kind: "offer", lost: true });
        return;
      }
      const t = now();
      const quote = checkoutQuote(pick.plan, pick.tier, draft, switches);
      const consent = renewalConsent(pick.plan, pick.tier, t, switches, venue.timezone);
      let answer: BuyAnswer;
      try {
        answer = buyAnswer(
          await client(tenantId, token).call(
            "membership.buy",
            buyBody(draft, quote, consent, buyId, returnTo(tenantId, buyId), cardOnPage),
          ),
        );
      } catch (err) {
        // B841 — only the door's own sentence is the buyer's; a dropped connection ("Failed to fetch") is the page's words.
        const said = err instanceof ApiError && err.message.trim() !== "" ? err.message : NOT_CONFIRMED;
        setView({ kind: "checkout", buyId, draft, pressed: true, busy: false, banner: { tone: "danger", text: from === "return" ? NOT_CONFIRMED : said } });
        return;
      }
      if (answer.kind === "bought") {
        storage()?.removeItem(stashKey(tenantId, buyId));
        setView({ kind: "done", answer, tierName: pick.tier.name, email: draft.email.trim() });
        return;
      }
      if (answer.kind === "card" && from === "press" && answer.refused === undefined) {
        // the draft waits here for the way back (a bank step may leave the page)
        storage()?.setItem(stashKey(tenantId, buyId), JSON.stringify(draft));
        if (answer.setupClientSecret !== undefined) {
          /* B816 — PAY: the card on THIS page; the purchase finishes the same way the way back does. */
          setView({ kind: "card", buyId, draft, clientSecret: answer.setupClientSecret, tierName: pick.tier.name, busy: false });
          return;
        }
        if (answer.setupUrl !== undefined) {
          // the provider's own card page (a stage without an in-page key)
          setView({ kind: "paying" });
          (props.go ?? ((u: string) => window.location.assign(u)))(answer.setupUrl);
          return;
        }
      }
      const text = answer.kind === "refused" ? answer.message : (answer.refused ?? CARD_NOT_SAVED);
      setView({ kind: "checkout", buyId, draft, pressed: true, busy: false, banner: { tone: answer.kind === "card" && answer.refused === undefined ? "info" : "danger", text } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seams are fixed for the page's life
    [tenantId, plans, token, switches, client, venue.timezone, cardOnPage, dialog],
  );

  // The way back from the card page: finish the purchase the stash names — once per finish
  // (B841: keyed by the view, so a refused finish's next PAY finishes again rather than standing on "Finishing…").
  const finishing = useRef<View | undefined>(undefined);
  useEffect(() => {
    if (view.kind !== "finishing" || plans === undefined || tenantId === undefined || finishing.current === view) return;
    finishing.current = view;
    const draft = readStash(storage()?.getItem(stashKey(tenantId, view.buyId)) ?? null);
    if (draft === undefined) {
      setView({ kind: "offer", lost: true });
      return;
    }
    void buy(view.buyId, draft, "return");
  }, [view, plans, tenantId, buy]);

  const offer = useMemo(() => (plans === undefined ? undefined : membershipsOffer(plans, term)), [plans, term]);
  const shownTerm = offer !== undefined && offer.terms.includes(term) ? term : (offer?.terms[0] ?? term);
  const choose = (picked: string): void => {
    const pick = plans === undefined ? undefined : tierPick(plans, picked, shownTerm);
    if (pick === undefined) return;
    setView({ kind: "checkout", buyId: newBuyId(), draft: freshDraft(pick.plan, pick.tier, { gift: giving }), pressed: false, busy: false });
  };
  /* B824 — in the frame the host page owns the tickets: no door navigates the frame to them. */
  const ticketsUrl = tenantId !== undefined && !dialog ? `/?tenant=${encodeURIComponent(tenantId)}` : undefined;
  const head = dialog ? (
    <SheetHead
      title="Memberships"
      redline="mships-sheet-header"
      titleRedline="mships-sheet-title"
      closeRedline="mships-sheet-close"
      backRedline="mships-sheet-back"
      onClose={() => bridge.current?.sendClose()}
      {...(view.kind === "checkout" ? { onBack: () => setView({ kind: "offer" }) } : {})}
    />
  ) : undefined;

  if (view.kind === "offer" || failed || plans === undefined || scope === "unknown") {
    const state: MembershipsState =
      scope === "unknown"
        ? { kind: "error", message: "This page doesn't know whose memberships to show — open it from the business's own site." }
        : failed
          ? { kind: "error", message: LOAD_REFUSED }
          : plans === undefined
            ? { kind: "loading" }
            : offer === undefined
              ? { kind: "empty" }
              : { kind: "ready", offer };
    const firstTier = offer?.tiers[0]?.pick ?? offer?.limited[0]?.pick;
    return (
      <MembershipsPage
        venue={brand}
        state={state}
        term={shownTerm}
        picked={picked ?? firstTier}
        onTerm={setTerm}
        gift={giving}
        onGift={setGiving}
        onPick={setPicked}
        onChoose={choose}
        onRetry={() => setAttempt((n) => n + 1)}
        ticketsUrl={ticketsUrl}
        head={head}
        {...(view.kind === "offer" && view.lost === true ? { notice: LOST } : {})}
      />
    );
  }

  // 636:2's bar: WHO'S COVERED goes back to the plans (‹ Memberships); DONE goes on to the tickets.
  const back =
    view.kind === "done"
      ? { url: ticketsUrl, label: "Tickets" }
      : view.kind === "checkout" && tenantId !== undefined && !dialog
        ? { url: `/memberships?tenant=${encodeURIComponent(tenantId)}`, label: "Memberships" }
        : undefined;
  const shell = (body: ReactElement): ReactElement => (
    <MembershipsShell venue={brand} backUrl={back?.url} backLabel={back?.label ?? "Tickets"} head={head}>
      {body}
    </MembershipsShell>
  );
  if (view.kind === "paying") return shell(<MembershipWorking at="PAY" text="Taking you to the secure card page…" />);
  if (view.kind === "card" && tenantId === undefined) return shell(<MembershipWorking at="PAY" text="Finishing your membership…" />);
  if (view.kind === "card")
    return shell(
      <MembershipCardStep
        tierName={view.tierName}
        note={cardStepNote({
          kind: "buy",
          renews: view.draft.gift !== undefined ? "gift" : view.draft.autoRenew ? "auto" : "manual",
          termWord: draftTier(plans, view.draft)?.plan.kind === "MONTHLY" ? "month" : "year",
        })}
        amount={(() => {
          const pick = draftTier(plans, view.draft);
          return pick === undefined ? undefined : formatMoney(checkoutQuote(pick.plan, pick.tier, view.draft, switches).total);
        })()}
        busy={view.busy}
        error={view.error}
        mount={(selector) => {
          const returnUrl = returnTo(tenantId ?? "", view.buyId);
          return (props.stripeFactory ?? (() => loadStripeFactory()))().then((factory) => {
            const made = createSetupGateway({
              factory,
              publishableKey: stripeFacts.publishableKey,
              ...(stripeFacts.stripeAccount !== undefined ? { stripeAccount: stripeFacts.stripeAccount } : {}),
              clientSecret: view.clientSecret,
              returnUrl,
              appearance: documentPaymentAppearance(),
            });
            if (made.gateway === undefined) return { unavailable: made.unavailable };
            made.gateway.mount(selector);
            return { gateway: made.gateway };
          });
        }}
        onPay={async (gateway) => {
          setView({ ...view, busy: true, error: undefined });
          const done = await gateway.confirm();
          if (!done.ok) {
            setView({ ...view, busy: false, error: done.message });
            return;
          }
          /* the way back's own leg finishes it from the stash (one buy, exactly once) */
          setView({ kind: "finishing", buyId: view.buyId });
        }}
        onBack={() => setView({ kind: "checkout", buyId: view.buyId, draft: view.draft, pressed: false, busy: false })}
      />,
    );
  if (view.kind === "finishing") return shell(<MembershipWorking at="DONE" text="Finishing your membership…" />);
  if (view.kind === "done") {
    return shell(
      <MembershipDone
        tierName={view.tierName}
        businessName={venue.businessName}
        memberNumber={view.answer.memberNumber}
        cards={view.answer.cards}
        autoRenew={view.answer.autoRenew}
        charged={view.answer.charged}
        email={view.email}
        gift={view.answer.gift}
        portalUrl={tenantId !== undefined ? `/me/membership?tenant=${encodeURIComponent(tenantId)}` : undefined}
        ticketsUrl={ticketsUrl}
        newTab={dialog}
      />,
    );
  }
  const pick = draftTier(plans, view.draft);
  if (pick === undefined) return shell(<MembershipWorking at="PLAN" text="That plan is no longer on sale." />);
  return shell(
    <CheckoutView
      view={view}
      plan={pick.plan}
      tier={pick.tier}
      switches={switches}
      now={now()}
      timeZone={venue.timezone}
      cardOnPage={cardOnPage}
      onDraft={(draft) => setView({ ...view, draft })}
      onBack={() => setView({ kind: "offer" })}
      onContinue={() => {
        const issues = checkoutIssues(pick.tier, view.draft, switches);
        if (Object.keys(issues).length > 0) {
          setView({ ...view, pressed: true });
          return;
        }
        setView({ ...view, pressed: true, busy: true });
        void buy(view.buyId, view.draft, "press");
      }}
    />,
  );
}

function CheckoutView(props: {
  readonly view: Extract<View, { kind: "checkout" }>;
  readonly plan: OfferPlan;
  readonly tier: OfferTier;
  readonly switches: BuyerSwitches;
  readonly now: number;
  readonly timeZone?: string | undefined;
  readonly cardOnPage: boolean;
  readonly onDraft: (d: CheckoutDraft) => void;
  readonly onBack: () => void;
  readonly onContinue: () => void;
}): ReactElement {
  const { view, plan, tier, switches } = props;
  return (
    <MembershipCheckout
      plan={plan}
      tier={tier}
      draft={view.draft}
      switches={switches}
      now={props.now}
      timeZone={props.timeZone}
      quote={checkoutQuote(plan, tier, view.draft, switches)}
      consent={renewalConsent(plan, tier, props.now, switches, props.timeZone)}
      issues={view.pressed ? checkoutIssues(tier, view.draft, switches) : {}}
      banner={view.banner}
      busy={view.busy}
      cardOnPage={props.cardOnPage}
      onDraft={props.onDraft}
      onContinue={props.onContinue}
      onBack={props.onBack}
    />
  );
}
